import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { GeneratedInsight } from '@/lib/insightNarrativeUtils';

interface InsightCardProps {
  insight: GeneratedInsight;
  index: number;
  onCtaClick?: (insight: GeneratedInsight) => void;
}

export function InsightCard({ insight, index, onCtaClick }: InsightCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.1,
        ease: 'easeOut'
      }}
    >
      <Card className="card-elevated hover:shadow-lg transition-shadow duration-200 h-full">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="text-3xl flex-shrink-0" role="img" aria-label={insight.title}>
              {insight.icon}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              <h3 className="font-semibold text-foreground leading-tight">
                {insight.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {insight.description}
              </p>
              
              {/* CTA */}
              {insight.cta && onCtaClick && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary hover:text-primary/80 font-medium text-sm"
                  onClick={() => onCtaClick(insight)}
                >
                  {insight.cta.text}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
